import { create } from "zustand";
import type {
  PropertyDefinition,
  PropertyState,
  PropertyActions,
} from "@/types/property";
import { useModelStore } from "./useModelStore";
import { useBeamSectionStore } from "@/hooks/useBeamSection";
import type { SectionGeometry } from "@/types/beamSection";
import { addData } from "@/services/centralized";
import { validatePropertyBeamReferences } from "../utils/structuralValidation";

/**
 * Convert property section data to BeamSection geometry
 */
const createSectionGeometry = (
  property: PropertyDefinition
): SectionGeometry | null => {
  if (!property.sectionProperties || !property.sectionType) {
    return null;
  }

  const sectionData: any = property.sectionProperties; // Type assertion for flexibility

  // Map section types to geometry types
  switch (property.sectionType) {
    case "I-Beam":
      return {
        type: "rolledSteel",
        designation: sectionData.designation || "",
        length: 1, // Will be set by beam length
        h: sectionData.h || 0,
        b: sectionData.b || 0,
        tf: sectionData.tf || 0,
        tw: sectionData.tw || 0,
        r1: sectionData.r1 || 0,
        r2: sectionData.r2 || 0,
        D: sectionData.D || 0,
        h1: sectionData.h1 || 0,
        h2: sectionData.h2 || 0,
        b1: sectionData.b1 || 0,
        C: sectionData.C || 0,
        g: sectionData.g || 0,
        g1Min: sectionData.g1Min || 0,
        maxRivet: sectionData.maxRivet || 0,
      };

    case "Channel":
      return {
        type: "rolledChannel",
        designation: sectionData.designation || "",
        length: 1,
        h: sectionData.h || 0,
        b: sectionData.b || 0,
        tf: sectionData.tf || 0,
        tw: sectionData.tw || 0,
        r1: sectionData.r1 || 0,
        r2: sectionData.r2 || 0,
        D: sectionData.D || 0,
        cyy: sectionData.cyy || 0,
        h1: sectionData.h1 || 0,
        h2: sectionData.h2 || 0,
        b1: sectionData.b1 || 0,
        C: sectionData.C || 0,
        g: sectionData.g || 0,
        g1Min: sectionData.g1Min || 0,
        maxRivet: sectionData.maxRivet || 0,
      };

    case "Equal Angle":
      return {
        type: "rolledEqualAngle",
        designation: sectionData.designation || "",
        length: 1,
        A: sectionData.A || 0,
        B: sectionData.B || 0,
        t: sectionData.t || 0,
        r1: sectionData.r1 || 0,
        r2: sectionData.r2 || 0,
        Cxx: sectionData.Cxx || 0,
        Cyy: sectionData.Cyy || 0,
        size: sectionData.size || "",
        exx: sectionData.exx || 0,
        eyy: sectionData.eyy || 0,
      };

    case "Unequal Angle":
      return {
        type: "rolledUnequalAngle",
        designation: sectionData.designation || "",
        length: 1,
        A: sectionData.A || 0,
        B: sectionData.B || 0,
        t: sectionData.t || 0,
        r1: sectionData.r1 || 0,
        r2: sectionData.r2 || 0,
        Cxx: sectionData.Cxx || 0,
        Cyy: sectionData.Cyy || 0,
        tanAlpha: sectionData.tanAlpha || 0,
        size: sectionData.size || "",
        exx: sectionData.exx || 0,
        eyy: sectionData.eyy || 0,
      };

    case "Tee Bar":
      return {
        type: "rolledTeeBar",
        designation: sectionData.designation || "",
        length: 1,
        h: sectionData.h || 0,
        b: sectionData.b || 0,
        tf: sectionData.tf || 0,
        tw: sectionData.tw || 0,
        r1: sectionData.r1 || 0,
        r2: sectionData.r2 || 0,
        D: sectionData.D || 0,
        Cxx: sectionData.Cxx || 0,
      };

    case "Bulb Angle":
      return {
        type: "rolledBulbAngle",
        designation: sectionData.designation || "",
        length: 1,
        h: sectionData.h || 0,
        b: sectionData.b || 0,
        tw: sectionData.tw || 0,
        tf: sectionData.tf || 0,
        D: sectionData.D || 0,
        r1: sectionData.r1 || 0,
        r2: sectionData.r2 || 0,
        r3: sectionData.r3 || 0,
        Cxx: sectionData.Cxx || 0,
        Cyy: sectionData.Cyy || 0,
        tanAlpha: sectionData.tanAlpha || 0,
        size: sectionData.size || "",
        exx: sectionData.exx || 0,
        eyy: sectionData.eyy || 0,
      };

    default:
      return null;
  }
};

/**
 * Property Store with Pending Assignments Support
 *
 * Manages property definitions and their assignments to beams.
 * Supports pending assignments for beams that don't exist yet.
 */
type PropertyStore = PropertyState &
  PropertyActions & {
    // Apply by Click mode state
    applyByClickMode: boolean;
    activePropertyIdForClick: string | null;
    setApplyByClickMode: (mode: boolean) => void;
    setActivePropertyIdForClick: (pId: string | null) => void;

    // Pending assignments tracking
    pendingAssignments: Map<string, string>; // beamId -> P_ID
    addPendingAssignment: (beamId: string, P_ID: string) => void;
    removePendingAssignment: (beamId: string) => void;
    autoApplyPendingProperties: () => void;
    clearAll: () => void;
  };

export const usePropertyStore = create<PropertyStore>((set, get) => ({
  // State
  properties: [],
  editingPropertyId: null,
  applyByClickMode: false,
  activePropertyIdForClick: null,
  pendingAssignments: new Map(),

  // Actions
  addProperty: (property: PropertyDefinition) => {
    const newProperty = {
      ...property,
      createdAt: property.createdAt || new Date().toISOString(),
      updatedAt: property.updatedAt || new Date().toISOString(),
    };

    set((state) => ({
      properties: [...state.properties, newProperty],
    }));

    // ✅ Apply to beams immediately after adding property
    if (property.beamIds && property.beamIds.length > 0) {
      // Use setTimeout to ensure the property is in state before applying
      setTimeout(() => {
        get().applyPropertyToBeams(property.P_ID, property.beamIds);
      }, 0);
    }
  },

  updateProperty: (P_ID: string, updates: Partial<PropertyDefinition>) => {
    set((state) => ({
      properties: state.properties.map((prop) =>
        prop.P_ID === P_ID
          ? { ...prop, ...updates, updatedAt: new Date().toISOString() }
          : prop
      ),
    }));
  },

  deleteProperty: (P_ID: string) => {
    // Remove property from store
    set((state) => ({
      properties: state.properties.filter((prop) => prop.P_ID !== P_ID),
    }));

    // Unassign property from all beams
    const modelStore = useModelStore.getState();

    const updatedBeams = modelStore.beams.map((beam) =>
      beam.P_ID === P_ID ? { ...beam, P_ID: "-" } : beam
    );
    modelStore.setBeams(updatedBeams);

    // Remove section visualization
    const beamSectionStore = useBeamSectionStore.getState();
    beamSectionStore.removeSection(P_ID);

    // Remove any pending assignments for this property
    set((state) => {
      const newPending = new Map(state.pendingAssignments);
      for (const [beamId, pId] of newPending.entries()) {
        if (pId === P_ID) {
          newPending.delete(beamId);
        }
      }
      return { pendingAssignments: newPending };
    });
  },

  getProperty: (P_ID: string) => {
    return get().properties.find((prop) => prop.P_ID === P_ID);
  },

  setEditingPropertyId: (P_ID: string | null) => {
    set({ editingPropertyId: P_ID });
  },

  applyPropertyToBeams: (P_ID: string, beamIds: string[]) => {
    const property = get().getProperty(P_ID);
    if (!property) {
      console.warn("⚠️ Property not found:", P_ID);
      return;
    }

    console.log("🔧 Applying property to beams:", { P_ID, beamIds, property });

    const modelStore = useModelStore.getState();
    const existingBeamIds = modelStore.beams.map((b) => b.id);

    // Validate ALL beams before ANY assignment
    const beamsWithProps = beamIds
      .map((beamId) => {
        const beam = modelStore.beams.find((b) => b.id === beamId);
        return beam?.P_ID &&
          beam.P_ID !== P_ID &&
          beam.P_ID !== "-" &&
          beam.P_ID !== "default"
          ? { beamId, propertyId: beam.P_ID }
          : null;
      })
      .filter(Boolean) as { beamId: string; propertyId: string }[];

    if (beamsWithProps.length > 0) {
      const conflicts = beamsWithProps
        .map((b) => `Beam ${b.beamId} (${b.propertyId})`)
        .join(", ");
      console.log(" BLOCKED by store validation:", conflicts);
      // Dispatch global error event for UI to catch
      window.dispatchEvent(
        new CustomEvent("propertyAssignmentBlocked", {
          detail: { beamIds: beamIds, conflicts, newPID: P_ID },
        })
      );
      return; // Early return blocks assignment
    }

    // Separate existing and pending beam IDs
    const availableBeamIds = beamIds.filter((id) =>
      existingBeamIds.includes(id)
    );

    if (availableBeamIds.length > 0) {
      const validationErrors = validatePropertyBeamReferences(
        [{ P_ID, beamIds: availableBeamIds }],
        existingBeamIds
      );
      if (validationErrors.length > 0) {
        console.warn(
          "⚠️ Property validation warning:",
          validationErrors[0].message
        );
        // Don't throw error - just warn and filter out invalid beams
        // This allows partial assignment to succeed
      }
    }

    const pendingBeamIds = beamIds.filter(
      (id) => !existingBeamIds.includes(id)
    );

    // REPLACE the property's beamIds (not merge) to support removal
    const updatedBeamIds = beamIds;

    // Find beams that need to be unassigned (were in property but not in new list)
    const beamsToUnassign = property.beamIds.filter(
      (oldBeamId) => !beamIds.includes(oldBeamId) && existingBeamIds.includes(oldBeamId)
    );

    set((state) => ({
      properties: state.properties.map((prop) =>
        prop.P_ID === P_ID
          ? {
              ...prop,
              beamIds: updatedBeamIds,
              updatedAt: new Date().toISOString(),
            }
          : prop
      ),
    }));

    // Apply to existing beams immediately
    if (availableBeamIds.length > 0) {
      const updatedBeams = modelStore.beams.map((beam) =>
        availableBeamIds.includes(beam.id) ? { ...beam, P_ID } : beam
      );
      modelStore.updateBeamsWithoutAutoApply(updatedBeams);
      console.log("✅ Applied to existing beams:", availableBeamIds);
    }

    // Unassign property from removed beams
    if (beamsToUnassign.length > 0) {
      const updatedBeams = modelStore.beams.map((beam) =>
        beamsToUnassign.includes(beam.id) && beam.P_ID === P_ID
          ? { ...beam, P_ID: "-" }
          : beam
      );
      modelStore.updateBeamsWithoutAutoApply(updatedBeams);
      console.log("✅ Unassigned property from beams:", beamsToUnassign);
    }

    // Track pending beam IDs for future assignment
    if (pendingBeamIds.length > 0) {
      pendingBeamIds.forEach((beamId) => {
        get().addPendingAssignment(beamId, P_ID);
      });
      console.log("⏳ Pending beam assignments:", pendingBeamIds, "→", P_ID);
    }

    // Create visual section geometry for available beams only
    const sectionGeometry = createSectionGeometry(property);
    console.log("🏗️ Created section geometry:", sectionGeometry);

    if (sectionGeometry && availableBeamIds.length > 0) {
      const beamSectionStore = useBeamSectionStore.getState();
      const now = Date.now();
      const existingSection = beamSectionStore.sections[P_ID];

      // Set section with ONLY the current beamIds (not merged)
      beamSectionStore.setSection(P_ID, {
        beamIds: availableBeamIds,
        geometry: sectionGeometry,
        color: existingSection?.color || "#4dabf7",
        rotation: existingSection?.rotation || 0,
        flipHorizontal: existingSection?.flipHorizontal || false,
        flipVertical: existingSection?.flipVertical || false,
        createdAt: existingSection?.createdAt || now,
        updatedAt: now,
      });
      console.log("✅ Updated section geometry for P_ID:", P_ID, {
        beamIds: availableBeamIds,
      });
    } else if (availableBeamIds.length === 0) {
      // Remove section if no beams are assigned
      const beamSectionStore = useBeamSectionStore.getState();
      beamSectionStore.removeSectionsForBeams(property.beamIds.filter(id => existingBeamIds.includes(id)));
      console.log("✅ Removed section geometry for P_ID:", P_ID);
    }
  },

  removePropertyFromBeams: (P_ID: string, beamIds: string[]) => {
    // Update the property's beamIds
    set((state) => ({
      properties: state.properties.map((prop) =>
        prop.P_ID === P_ID
          ? {
              ...prop,
              beamIds: prop.beamIds.filter((id) => !beamIds.includes(id)),
              updatedAt: new Date().toISOString(),
            }
          : prop
      ),
    }));

    // Update the beams in ModelStore (set P_ID to '-')
    const modelStore = useModelStore.getState();
    const updatedBeams = modelStore.beams.map((beam) =>
      beamIds.includes(beam.id) && beam.P_ID === P_ID
        ? { ...beam, P_ID: "-" }
        : beam
    );
    modelStore.setBeams(updatedBeams);

    // Update section visualization
    const beamSectionStore = useBeamSectionStore.getState();
    const existingSection = beamSectionStore.sections[P_ID];

    if (existingSection) {
      const updatedSectionBeamIds = existingSection.beamIds.filter(
        (id) => !beamIds.includes(id)
      );

      if (updatedSectionBeamIds.length === 0) {
        // Remove section if no beams left
        beamSectionStore.removeSection(P_ID);
      } else {
        // Update section with remaining beams
        beamSectionStore.setSection(P_ID, {
          ...existingSection,
          beamIds: updatedSectionBeamIds,
          updatedAt: Date.now(),
        });
      }
    }
  },

  getBeamsWithProperty: (P_ID: string) => {
    const modelStore = useModelStore.getState();
    return modelStore.beams
      .filter((beam) => beam.P_ID === P_ID)
      .map((beam) => beam.id);
  },

  // Apply by Click mode actions
  setApplyByClickMode: (mode: boolean) => {
    set({ applyByClickMode: mode });
  },

  setActivePropertyIdForClick: (pId: string | null) => {
    set({ activePropertyIdForClick: pId });
  },

  // ✅ Pending assignments management
  addPendingAssignment: (beamId: string, P_ID: string) => {
    set((state) => {
      const newPending = new Map(state.pendingAssignments);
      newPending.set(beamId, P_ID);
      console.log(`⏳ Added pending assignment: Beam ${beamId} → P_ID ${P_ID}`);
      return { pendingAssignments: newPending };
    });
  },

  removePendingAssignment: (beamId: string) => {
    set((state) => {
      const newPending = new Map(state.pendingAssignments);
      newPending.delete(beamId);
      return { pendingAssignments: newPending };
    });
  },

  // ✅ Auto-apply pending properties when beams are created
  autoApplyPendingProperties: () => {
    const modelStore = useModelStore.getState();
    const pending = get().pendingAssignments;

    if (pending.size === 0) {
      console.log("✅ No pending property assignments to apply");
      return;
    }

    console.log(
      "🔍 Checking pending property assignments...",
      Array.from(pending.entries())
    );

    const appliedBeams: { beamId: string; P_ID: string }[] = [];
    const updatedBeams = modelStore.beams.map((beam) => {
      const pendingPID = pending.get(beam.id);
      if (pendingPID && beam.P_ID !== pendingPID) {
        appliedBeams.push({ beamId: beam.id, P_ID: pendingPID });
        return { ...beam, P_ID: pendingPID };
      }
      return beam;
    });

    if (appliedBeams.length > 0) {
      // Update beams with properties
      modelStore.setBeams(updatedBeams);

      // Update property beamIds in store
      appliedBeams.forEach(({ beamId, P_ID }) => {
        const property = get().getProperty(P_ID);
        if (property && !property.beamIds.includes(beamId)) {
          set((state) => ({
            properties: state.properties.map((prop) =>
              prop.P_ID === P_ID
                ? {
                    ...prop,
                    beamIds: [...prop.beamIds, beamId],
                    updatedAt: new Date().toISOString(),
                  }
                : prop
            ),
          }));
        }
      });

      // Remove applied beams from pending
      set((state) => {
        const newPending = new Map(state.pendingAssignments);
        appliedBeams.forEach(({ beamId }) => newPending.delete(beamId));
        return { pendingAssignments: newPending };
      });

      // Update section visualizations grouped by P_ID
      const beamsByProperty = new Map<string, string[]>();
      appliedBeams.forEach(({ beamId, P_ID }) => {
        if (!beamsByProperty.has(P_ID)) {
          beamsByProperty.set(P_ID, []);
        }
        beamsByProperty.get(P_ID)!.push(beamId);
      });

      beamsByProperty.forEach((beamIds, P_ID) => {
        const property = get().getProperty(P_ID);
        if (property) {
          const sectionGeometry = createSectionGeometry(property);
          if (sectionGeometry) {
            const beamSectionStore = useBeamSectionStore.getState();
            const existingSection = beamSectionStore.sections[P_ID];

            if (existingSection) {
              // Add to existing section
              const mergedBeamIds = Array.from(
                new Set([...existingSection.beamIds, ...beamIds])
              );
              beamSectionStore.setSection(P_ID, {
                ...existingSection,
                beamIds: mergedBeamIds,
                updatedAt: Date.now(),
              });
              console.log(
                `✅ Updated section for P_ID ${P_ID} with new beams:`,
                beamIds
              );
            } else {
              // Create new section
              beamSectionStore.setSection(P_ID, {
                beamIds: beamIds,
                geometry: sectionGeometry,
                color: "#4dabf7",
                rotation: 0,
                flipHorizontal: false,
                flipVertical: false,
                createdAt: Date.now(),
                updatedAt: Date.now(),
              });
              console.log(
                `✅ Created section for P_ID ${P_ID} with beams:`,
                beamIds
              );
            }
          }
        }
      });

      console.log(
        `✅ Auto-applied ${appliedBeams.length} pending property assignments:`,
        appliedBeams
      );
    } else {
      console.log("ℹ️ No pending assignments matched current beams");
    }
  },

  // ✅ Clear all properties and pending assignments
  clearAll: () => {
    console.log("🧹 Clearing all properties and pending assignments");

    // Clear all section visualizations
    const beamSectionStore = useBeamSectionStore.getState();
    Object.keys(beamSectionStore.sections).forEach((P_ID) => {
      beamSectionStore.removeSection(P_ID);
    });

    // Reset store state
    set({
      properties: [],
      editingPropertyId: null,
      applyByClickMode: false,
      activePropertyIdForClick: null,
      pendingAssignments: new Map(),
    });
  },
}));

// Subscribe to PropertyStore and publish to centralized store
usePropertyStore.subscribe((state) => {
  addData("properties", state.properties);
  console.log(
    "✅ [PropertyStore→Centralized] Published properties:",
    state.properties.length
  );
});
